/**
 * QuotaWarningBanner - 50% threshold informational banner
 *
 * Features:
 * - Subtle blue informational style
 * - Dismissible with X button
 * - Auto-dismiss after 10 seconds
 * - Shows remaining quota and reset time
 */
import { useState, useEffect } from 'react';
import { View, Text, Pressable } from 'react-native';
import { useTranslation } from '@dramadetector/shared';

interface QuotaWarningBannerProps {
  dailyAnalysesUsed: number;
  dailyAnalysesLimit: number;
  resetTime: string;
  onDismiss: () => void;
}

export function QuotaWarningBanner({
  dailyAnalysesUsed,
  dailyAnalysesLimit,
  resetTime,
  onDismiss,
}: QuotaWarningBannerProps) {
  const { t } = useTranslation('subscription');
  const [visible, setVisible] = useState(true);

  // Auto-dismiss after 10 seconds
  useEffect(() => {
    const timer = setTimeout(() => {
      handleDismiss();
    }, 10000);

    return () => clearTimeout(timer);
  }, []);

  const handleDismiss = () => {
    setVisible(false);
    onDismiss();
  };

  if (!visible) return null;

  return (
    <View className="bg-blue-50 border border-blue-200 rounded-lg p-4 max-w-md mx-auto mb-3">
      <View className="flex-row items-start">
        {/* Info icon */}
        <Text className="text-xl mr-3">ℹ️</Text>

        {/* Content */}
        <View className="flex-1">
          <Text className="text-sm font-medium text-blue-900 mb-1">
            {t('quota.warning.banner.title')}
          </Text>
          <Text className="text-xs text-blue-700">
            {t('quota.warning.banner.description', {
              used: dailyAnalysesUsed,
              limit: dailyAnalysesLimit,
              resetTime
            })}
          </Text>
        </View>

        {/* Dismiss button */}
        <Pressable
          onPress={handleDismiss}
          className="ml-2 w-6 h-6 items-center justify-center active:opacity-50"
          accessibilityRole="button"
          accessibilityLabel={t('quota.warning.banner.accessibility.dismiss')}
          hitSlop={{ top: 10, bottom: 10, left: 10, right: 10 }}
        >
          <Text className="text-blue-400 text-lg">×</Text>
        </Pressable>
      </View>
    </View>
  );
}
